import campaignController from '.';
import { campaignDebug } from './diagnostics';
import { campaignParentOrigin, isCampaignMessageType, isTrustedCampaignOrigin } from './originCheck';
import webActivityController from '../webActivity';
import { collectEnrichment } from '../webActivity/enrichment';

/**
 * IMBEE-8954 (REQ-CWC-02) — the widget half of the loader<->widget campaign channel.
 *
 * The loader queues every campaign message until the widget says it is ready, because a message
 * posted into an iframe with no `message` listener is gone with no error, and on a non-SPA page
 * the initial `IMBEE_PAGE_CONTEXT` is the only one the visit will ever produce.
 *
 * The readiness signal therefore has ONE job: to be true. It is posted from this function, on the
 * statement AFTER `addEventListener`, so "ready" means exactly "a listener is registered and will
 * receive what you send next" — the only claim the loader's queue can safely act on.
 *
 * That is why it is not `widgetConfig`. `widgetConfig` is a LAYOUT message: it is posted from
 * `GET /widget`'s handler, it carries the launcher geometry, and it is posted only `if
 * (data.logoConfig)`. Reusing it as the readiness signal made the loader flush into a window
 * whose listener would not be mounted for another three awaited calls (`fetchVisitorNonce` ->
 * `fetchAccessToken` -> `loadSession`, then `isValidWidget`), and made campaigns depend on a
 * tenant having uploaded a logo. Both are properties of a message that was never about readiness.
 *
 * Mounted by `App` on its first commit, NOT under `isValidWidget`: the controller is deliberately
 * order-independent (`CampaignController.setEnabled` re-evaluates a page context it was given
 * before `GET /widget` resolved), so the earliest possible listener is also the safest one.
 */
export const CAMPAIGN_CHANNEL_READY = 'campaignChannelReady';

export const installCampaignMessageChannel = (parentUrl: string): (() => void) => {
  const listener = (event: MessageEvent) => {
    if (!isCampaignMessageType(event.data?.type)) return;
    // REQ-CWC-02 — the three campaign message types are trusted only from the loader's own
    // origin. `EXTERNAL_MESSAGE` keeps its own pre-existing, origin-unchecked listener.
    if (!isTrustedCampaignOrigin(event.origin, parentUrl)) return;

    const { type, url, referrer, isRouteChange } = event.data ?? {};
    switch (type) {
      case 'IMBEE_PAGE_CONTEXT': {
        if (typeof url !== 'string') return;
        const pageContext = {
          url,
          referrer: typeof referrer === 'string' ? referrer : '',
          isRouteChange: Boolean(isRouteChange),
        };
        campaignController.onPageContext(pageContext);
        // IMBEE-8954 / T113 — the same trusted page context also drives the website-activity
        // beacon (TDS §6f). This is the only seam where the CUSTOMER's page URL reaches the widget,
        // and the widget is the only place `visitorToken` can be read, so the beacon must be
        // emitted from here. Enrichment is collected once per page LOAD, not per route change:
        // those fields are `$setOnInsert` or stable server-side.
        webActivityController.onPageContext(
          pageContext,
          pageContext.isRouteChange ? undefined : collectEnrichment(url, pageContext.referrer),
        );
        return;
      }
      case 'IMBEE_EXIT_INTENT':
        campaignController.onExitIntent();
        return;
      default:
    }
  };

  window.addEventListener('message', listener);

  // Announced only now, and only to the loader's own origin. A widget booted outside a loader
  // (`parent` absent — the CMS preview, a direct visit) has nobody to announce to, and there is
  // no campaign channel to be ready on.
  //
  // Gated on the parent being PARSEABLE, not merely present: `postMessage` throws a `SyntaxError`
  // on a target origin it cannot parse, and a truthy-but-malformed `parent` (`?parent=not a url`)
  // is exactly that. Thrown here — inside an effect on the app's first commit, with no error
  // boundary above it — it unmounted the entire widget rather than skipping one announcement.
  const parentOrigin = campaignParentOrigin(parentUrl);
  if (parentOrigin && window.parent) {
    window.parent.postMessage({ name: CAMPAIGN_CHANNEL_READY }, parentOrigin);
  } else {
    campaignDebug('campaignChannelListeningWithoutParent', { parentUrl });
  }

  return () => window.removeEventListener('message', listener);
};
